import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LogOut, Gauge, Table2, Flag, FileText, Users, FolderKanban, Files, History, Loader2,
  Building2, FlaskConical, ShieldCheck,
} from "lucide-react";
import { FIRMA, LOGOS, ROLE_BADGE, ROLE_LABEL, ROLE_LOGO } from "@/lib/constants";
import { NdaBanner } from "@/components/NdaBanner";
import { GuidaPagina } from "@/components/Guida";

const TABS = [
  { to: "/dashboard", label: "Dashboard", icon: Gauge, testid: "nav-dashboard-tab", roles: ["admin", "pm", "dev", "tester", "fornitore", "cliente_finale"] },
  { to: "/piano-interno", label: "Piano Interno", icon: Table2, testid: "nav-piano-interno-tab", roles: ["admin"] },
  { to: "/area-dev", label: "Area Dev", icon: Table2, testid: "nav-area-dev-tab", roles: ["dev"] },
  { to: "/area-pm", label: "Piano di progetto", icon: Table2, testid: "nav-area-pm-tab", roles: ["pm"] },
  { to: "/area-test", label: "Area Test", icon: FlaskConical, testid: "nav-area-test-tab", roles: ["admin", "dev", "tester"] },
  { to: "/area-fornitore", label: "I miei task", icon: Table2, testid: "nav-area-fornitore-tab", roles: ["admin", "fornitore"] },
  { to: "/milestone-cliente", label: "Milestone Cliente", icon: Flag, testid: "nav-milestone-cliente-tab", roles: ["admin", "pm", "dev", "cliente_finale"] },
  { to: "/sprint-review", label: "Sprint Review", icon: FileText, testid: "nav-sprint-review-tab", roles: ["admin", "pm", "dev", "tester", "fornitore", "cliente_finale"] },
  { to: "/documenti", label: "Documenti", icon: Files, testid: "nav-documenti-tab", roles: ["admin", "pm", "dev", "tester", "fornitore", "cliente_finale"] },
  { to: "/qualita", label: "Qualità & Test", icon: ShieldCheck, testid: "nav-qualita-tab", roles: ["admin", "pm", "dev", "tester", "cliente_finale"] },
  { to: "/storico", label: "Storico", icon: History, testid: "nav-storico-tab", roles: ["admin", "pm", "dev", "tester", "fornitore", "cliente_finale"] },
  { to: "/organizzazioni", label: "Clienti & Aree", icon: Building2, testid: "nav-organizzazioni-tab", roles: ["admin"] },
  { to: "/progetti", label: "Progetti", icon: FolderKanban, testid: "nav-progetti-tab", roles: ["admin"] },
  { to: "/utenti", label: "Profili", icon: Users, testid: "nav-utenti-tab", roles: ["admin"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { projects, projectId, selectProject } = useProject();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const areaLogo = ROLE_LOGO[user.role] || LOGOS.governance;
  const tabs = TABS.filter((t) => t.roles.includes(user.role));

  return (
    <div className="min-h-screen bg-[#F4F6F9]">
      <header className="sticky top-0 z-40 border-b border-slate-800 bg-slate-900">
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
          <img src={LOGOS.governance} alt="Governance Hub" className="h-9 w-9 shrink-0 rounded-lg bg-white object-cover" />
          <div className="mr-auto min-w-0">
            <p className="truncate text-base font-bold tracking-tight text-white">Governance Hub</p>
            <p className="hidden font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 sm:block">
              {FIRMA}
            </p>
          </div>

          {projects === null ? (
            <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
          ) : (
            <Select value={projectId} onValueChange={selectProject}>
              <SelectTrigger
                data-testid="project-select"
                className="h-9 w-[170px] border-slate-700 bg-slate-800 text-sm text-white sm:w-[220px]"
              >
                <SelectValue placeholder="Progetto" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="flex items-center gap-2.5">
            <img src={areaLogo} alt={ROLE_LABEL[user.role]} className="hidden h-9 w-9 rounded-lg bg-white object-cover sm:block" />
            <div className="hidden text-right lg:block">
              <p className="max-w-[170px] truncate text-sm font-semibold text-white" data-testid="current-user-name">
                {user.name}
              </p>
              <span className={`inline-block rounded px-1.5 py-0.5 font-mono text-[10px] ${ROLE_BADGE[user.role]}`} data-testid="current-user-role">
                {ROLE_LABEL[user.role]}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={doLogout}
              data-testid="logout-button"
              className="text-slate-300 hover:bg-slate-800 hover:text-white"
            >
              <LogOut className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Esci</span>
            </Button>
          </div>
        </div>

        <nav className="border-t border-slate-800 bg-slate-950/40">
          <div className="mx-auto flex max-w-[1600px] gap-0.5 overflow-x-auto px-2 sm:px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {tabs.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                data-testid={t.testid}
                className={({ isActive }) =>
                  `flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "border-amber-400 text-white"
                      : "border-transparent text-slate-400 hover:text-slate-100"
                  }`
                }
              >
                <t.icon className="h-4 w-4" />
                {t.label}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>

      <main className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6 sm:py-8">
        <GuidaPagina />
        <Outlet />
        <NdaBanner />
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center justify-between gap-2 px-4 py-5 sm:px-6">
          <p className="text-xs text-slate-500">
            Governance Hub · Stefano Critelli → Majorbit → Infocamere
          </p>
          <p className="font-mono text-[11px] font-semibold text-slate-900" data-testid="stefano-critelli-signature">
            {FIRMA}
          </p>
        </div>
      </footer>
    </div>
  );
}
